/**
 * IMP-2574 — the "basic" message format: **bold** and *italic*, and nothing else.
 *
 * Staff bulk messages are stored in this format with `contentFormat: 'basic'`. Every
 * other message has no `contentFormat` and stays plain text, so a participant typing
 * `2*3*4` is never reinterpreted.
 *
 * This is the web copy of Platform-CMS-Backend's src/utils/helpers/basicMessageFormat.ts.
 * The backend, CMS, web and mobile copies must parse identically, and are all checked
 * against the same test vectors (here, src/utils/basicMessageFormat.vectors.json):
 *
 * - `**` toggles bold and `*` toggles italic. They can nest inside each other.
 * - An opening marker must be followed by a non-space character, and a closing marker
 *   preceded by one, so `2 * 3 * 4` stays literal.
 * - Formatting never crosses a line break. A marker left unclosed at the end of a line
 *   is literal text.
 * - `\*` is a literal asterisk and `\\` a literal backslash. Any other backslash is
 *   literal.
 * - Formatting with no text inside it (`****`) is dropped.
 */
export const MessageContentFormat = {
  BASIC: 'basic',
};

const MARKER_LENGTH = { bold: 2, italic: 1 };

const isSpace = ch => ch === undefined || /\s/.test(ch);

/** Splits one line into text and marker tokens, pairing openers with closers. */
function tokenizeLine(line) {
  const tokens = [];
  const stack = [];
  let text = '';
  const flushText = () => {
    if (text) tokens.push({ kind: 'text', value: text });
    text = '';
  };
  let i = 0;
  while (i < line.length) {
    const ch = line[i];
    if (ch === '\\' && (line[i + 1] === '*' || line[i + 1] === '\\')) {
      text += line[i + 1];
      i += 2;
      continue;
    }
    if (ch !== '*') {
      text += ch;
      i += 1;
      continue;
    }
    let runEnd = i;
    while (line[runEnd] === '*') runEnd += 1;
    let remaining = runEnd - i;
    const canClose = !isSpace(line[i - 1]);
    const canOpen = !isSpace(line[runEnd]);
    // An opener pushed by this same run can be closed by the rest of the run even
    // though there's no text between them; that empty pair is dropped later.
    let openedInRun = 0;
    flushText();
    while (remaining > 0) {
      const top = stack[stack.length - 1];
      const topLength = top ? MARKER_LENGTH[top.marker] : 0;
      if (top && (canClose || openedInRun > 0) && remaining >= topLength) {
        top.matched = true;
        stack.pop();
        tokens.push({ kind: 'close', marker: top.marker });
        remaining -= topLength;
        if (openedInRun > 0) openedInRun -= 1;
        continue;
      }
      const marker = remaining >= 2 ? 'bold' : 'italic';
      if (canOpen && !stack.some(open => open.marker === marker)) {
        const open = { kind: 'open', marker, matched: false };
        stack.push(open);
        tokens.push(open);
        remaining -= MARKER_LENGTH[marker];
        openedInRun += 1;
        continue;
      }
      tokens.push({ kind: 'text', value: '*'.repeat(remaining) });
      remaining = 0;
    }
    i = runEnd;
  }
  flushText();
  return tokens;
}

/** Parses basic-format text into styled spans. Unmatched markers become plain text. */
export function parseBasicFormat(source) {
  const spans = [];
  const push = (text, bold, italic) => {
    if (!text) return;
    const last = spans[spans.length - 1];
    if (last && last.bold === bold && last.italic === italic) {
      last.text += text;
    } else {
      spans.push({ text, bold, italic });
    }
  };
  (source ?? '').split('\n').forEach((line, lineIndex) => {
    if (lineIndex > 0) push('\n', false, false);
    let bold = false;
    let italic = false;
    tokenizeLine(line).forEach(token => {
      if (token.kind === 'text') {
        push(token.value, bold, italic);
      } else if (token.kind === 'open' && !token.matched) {
        push('*'.repeat(MARKER_LENGTH[token.marker]), bold, italic);
      } else if (token.marker === 'bold') {
        bold = token.kind === 'open';
      } else {
        italic = token.kind === 'open';
      }
    });
  });
  return spans;
}

/**
 * Escapes text so it is shown literally in basic format. Used for substituted template
 * values. A backslash is only escaped where it could be read as an escape: before `*`,
 * before another backslash, or at the end, where a marker may follow.
 */
export function escapeBasicFormat(text) {
  return (text ?? '').replace(/\*|\\(?=[\\*]|$)/g, ch => `\\${ch}`);
}

/**
 * Writes spans back out as basic-format text. A span's own whitespace edges are kept
 * outside its markers, so `** a **`-style output that wouldn't parse is never produced.
 */
function serializeSpans(spans) {
  return spans
    .map(span => {
      if (!span.bold && !span.italic) return escapeBasicFormat(span.text);
      return span.text
        .split('\n')
        .map(line => {
          const [, lead, core, trail] = /^(\s*)([\s\S]*?)(\s*)$/.exec(line) ?? ['', '', line, ''];
          if (!core) return line;
          const open = `${span.bold ? '**' : ''}${span.italic ? '*' : ''}`;
          const close = `${span.italic ? '*' : ''}${span.bold ? '**' : ''}`;
          return `${lead}${open}${escapeBasicFormat(core)}${close}${trail}`;
        })
        .join('\n');
    })
    .join('');
}

/**
 * Rewrites basic-format text into its canonical form: markers that don't pair up are
 * escaped, empty formatting is dropped, and nothing but bold and italic survives.
 * Applying it twice gives the same result.
 *
 * `removedMark`, when given, is a character the caller left where text was removed
 * (e.g. a stripped template field). It is deleted after parsing, so formatting that
 * wrapped only that text, like `*{{Field}}*`, is dropped instead of leaving `**`.
 */
export function normalizeBasicFormat(source, removedMark) {
  let spans = parseBasicFormat(source);
  if (removedMark) {
    spans = spans.reduce((kept, span) => {
      const text = span.text.split(removedMark).join('');
      const last = kept[kept.length - 1];
      if (!text) return kept;
      if (last && last.bold === span.bold && last.italic === span.italic) {
        last.text += text;
      } else {
        kept.push({ ...span, text });
      }
      return kept;
    }, []);
  }
  return serializeSpans(spans);
}

/** The text a reader sees, with every marker and escape removed. */
export function stripBasicFormat(source) {
  return parseBasicFormat(source)
    .map(span => span.text)
    .join('');
}

/**
 * A message's text with formatting removed when it is basic format, for previews, push
 * and other plain-text channels. Any other message is returned as it is.
 */
export function toPlainMessageText(content, contentFormat) {
  if (!content) return '';
  return contentFormat === MessageContentFormat.BASIC ? stripBasicFormat(content) : content;
}
